import { computed, ref } from 'vue'
import type Echo from 'laravel-echo'
import type { ItemImage, ListItem } from '~/types'

export type ConnectionState = 'connected' | 'connecting' | 'reconnecting' | 'disconnected'

interface ListChannelHandlers {
    onItemAdded?: (item: ListItem) => void
    onItemUpdated?: (item: ListItem) => void
    onItemToggled?: (payload: {
        item_id: number
        is_completed: boolean
        completed_at: string | null
    }) => void
    onItemDeleted?: (payload: { item_id: number }) => void
    onItemsReordered?: (payload: { ordered_ids: number[] }) => void
    onItemImageUpdated?: (payload: { item_id: number; image: ItemImage | null }) => void
    onListCompleted?: (payload: { list_uuid: string; completed_by: string | null }) => void
}

// Module-level connection tracking (singleton across all useRealtime() calls)
const _connectionState = ref<ConnectionState>('disconnected')
const _lastEventAt = ref<number | null>(null)
let _initialized = false
const _wasEverConnected = ref(false)

function wireConnectionTracking(instance: Echo<'reverb'>): void {
    // Pusher v7 exposes .pusher.connection; Pusher v8+ exposes .connection directly
    const connector = instance.connector as
        | {
              pusher?: {
                  connection?: {
                      state: string
                      bind: (event: string, cb: (...args: unknown[]) => void) => void
                  }
              }
              connection?: {
                  state: string
                  bind: (event: string, cb: (...args: unknown[]) => void) => void
              }
          }
        | undefined
    const pusherConnection = connector?.pusher?.connection ?? connector?.connection

    if (!pusherConnection || _initialized) return
    _initialized = true
    function resolveState(raw: string): ConnectionState {
        if (raw === 'connected') {
            _wasEverConnected.value = true
            return 'connected'
        }
        const prev = _connectionState.value
        // Transitioning away from connected = reconnecting
        if (prev === 'connected' && (raw === 'connecting' || raw === 'unavailable')) {
            return 'reconnecting'
        }
        if (raw === 'connecting' || raw === 'initialized') return 'connecting'
        return 'disconnected'
    }

    const current = pusherConnection.state
    if (current && typeof current === 'string') {
        _connectionState.value = resolveState(current)
    }

    pusherConnection.bind('state_change', (states: unknown) => {
        const s = states as { current: string; previous: string }
        _connectionState.value = resolveState(s.current)
        if (s.current === 'connected') {
            _lastEventAt.value = Date.now()
        }
    })

    pusherConnection.bind('connected', () => {
        _lastEventAt.value = Date.now()
    })
}

function markActivity(): void {
    _lastEventAt.value = Date.now()
}

export function useRealtime() {
    const nuxtApp = useNuxtApp()

    function echo(): Echo<'reverb'> | null {
        const instance = (nuxtApp.$echo as Echo<'reverb'> | undefined | null) ?? null
        if (instance) wireConnectionTracking(instance)
        return instance
    }

    function subscribeToList(uuid: string, handlers: ListChannelHandlers): () => void {
        const instance = echo()

        if (!instance) {
            return () => {}
        }

        const channel = instance.channel(`list.${uuid}`)

        channel
            .listen('ItemAdded', (payload: { item: ListItem }) => {
                markActivity()
                handlers.onItemAdded?.(payload.item)
            })
            .listen('ItemUpdated', (payload: { item: ListItem }) => {
                markActivity()
                handlers.onItemUpdated?.(payload.item)
            })
            .listen(
                'ItemToggled',
                (payload: {
                    item_id: number
                    is_completed: boolean
                    completed_at: string | null
                }) => {
                    markActivity()
                    handlers.onItemToggled?.(payload)
                },
            )
            .listen('ItemDeleted', (payload: { item_id: number }) => {
                markActivity()
                handlers.onItemDeleted?.(payload)
            })
            .listen('ItemsReordered', (payload: { ordered_ids: number[] }) => {
                markActivity()
                handlers.onItemsReordered?.(payload)
            })
            .listen('ItemImageUpdated', (payload: { item_id: number; image: ItemImage | null }) => {
                markActivity()
                handlers.onItemImageUpdated?.(payload)
            })
            .listen(
                'ListCompleted',
                (payload: { list_uuid: string; completed_by: string | null }) => {
                    markActivity()
                    handlers.onListCompleted?.(payload)
                },
            )

        return () => instance.leaveChannel(`list.${uuid}`)
    }

    function subscribeToUser(
        userId: number,
        onNotification: (notification: Record<string, unknown>) => void,
    ): () => void {
        const instance = echo()

        if (!instance) {
            return () => {}
        }

        instance.private(`App.Models.User.${userId}`).notification(onNotification)

        return () => instance.leaveChannel(`private-App.Models.User.${userId}`)
    }

    return {
        subscribeToList,
        subscribeToUser,
        connected: computed(() => echo() !== null),
        connectionState: computed(() => _connectionState.value),
        lastEventAt: computed(() => _lastEventAt.value),
        wasEverConnected: computed(() => _wasEverConnected.value),
    }
}
